import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { finesService, errorMessage } from '../../../services/finesService';
import { money, KIND, Notice, Tile } from './collectionsShared';
import { shown, when, who, day, matches, summary, NO_FILTERS, RESULT_FILTERS } from './followUp.logic';
import Button from '../../../ui/Button';
import Badge from '../../../ui/Badge';
import StatusBadge from '../../../ui/Status/StatusBadge';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import EmptyState from '../../../ui/EmptyState';
import { Field, Input, Select } from '../../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellId, CellNumber, CellMuted, toneClass } from '../../../ui/DataTable/cells';
import LoadingSpinner from '../../../components/LoadingSpinner';

// The "Reminder result" (owner, 23 Oct 2026): every contract the branch reminder e-mails asked to charge, with what
// Ejar showed on its last day (day 14) - the same rows as the follow-up e-mail to the managers - and, once the block
// has expired, its final result. Built by the engine (/api/collections/day13/followup). reloadKey: changes with each
// build of the day-13 list (Rebuild from Ejar too) and each mark's Ejar re-read - the engine updates these results
// from those reads, so they are loaded again.
const HEADS = ['#', 'Follow-up', 'Branch', 'Contract', 'Customer', 'Deposit', 'Sent on', 'To charge', 'Charged',
    'Charged on / by / method', 'Still to charge', 'Result'];
const DAYS = [['7', 'Last 7 days'], ['14', 'Last 14 days'], ['30', 'Last 30 days'], ['60', 'Last 60 days']];

export default function Day13FollowUp({ reloadKey = '' }) {
    const [days, setDays] = useState('14');
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    const [f, setF] = useState(NO_FILTERS);

    const load = useCallback(async (n) => {
        setBusy('Loading the reminder results…'); setErr('');
        try { setRows((await finesService.collectionsFollowUp(Number(n) || 14)).rows || []); } catch (e) { setErr(errorMessage(e)); }
        finally { setBusy(''); }
    }, []);
    useEffect(() => { load(days); }, [load, days, reloadKey]);

    const list = useMemo(() => (rows || []).filter((r) => matches(r, f)), [rows, f]);
    const sm = summary(list);
    const filtered = f.branch !== 'all' || f.result !== 'all' || !!f.q;
    const setOne = (k, v) => setF((x) => ({ ...x, [k]: v }));

    return (
        <div className="flex flex-col gap-3">
            <div>
                <h4 className="text-sm font-semibold text-gray-900">Reminder result</h4>
                <p className="text-xs text-gray-600">
                    Each contract the branch e-mails asked to charge, as Ejar showed it at 10:00 on its last day (day 14) -
                    the follow-up e-mail to the managers, the branch in CC, goes when something is not fully charged. Charged
                    later: it shows within the hour (Rebuild from Ejar shows it at once until the final read). The next day
                    the ones not fully charged are read from Ejar again: that is the final result (the block has expired).
                </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <Field label="Period" className="w-40">
                    <Select size="sm" value={days} onChange={(e) => setDays(e.target.value)} options={DAYS.map(([value, label]) => ({ value, label }))} />
                </Field>
                <Field label="Branch" className="w-32">
                    <Select size="sm" value={f.branch} onChange={(e) => setOne('branch', e.target.value)}
                        options={[{ value: 'all', label: 'All' }, { value: 'T1', label: 'T1' }, { value: 'T2', label: 'T2' }]} />
                </Field>
                <Field label="Result" className="w-60">
                    <Select size="sm" value={f.result} onChange={(e) => setOne('result', e.target.value)} options={RESULT_FILTERS.map(([value, label]) => ({ value, label }))} />
                </Field>
                <Field label="Search" className="w-64">
                    <Input size="sm" value={f.q} placeholder="Contract, customer or plate" onChange={(e) => setOne('q', e.target.value)} />
                </Field>
                {filtered && <Button size="sm" variant="ghost" onClick={() => setF(NO_FILTERS)}>Clear filters</Button>}
                <Button size="sm" onClick={() => load(days)} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Reload</Button>
                {rows && <span className="text-sm text-gray-700 self-center">Showing {list.length} of {rows.length}</span>}
            </div>
            {busy && <div className="flex items-center gap-2 text-sm text-gray-700" role="status"><LoadingSpinner size="sm" />{busy}</div>}
            {err && <Notice tone="danger">{err}</Notice>}
            {rows && (
                <>
                    <div className="flex flex-wrap gap-3">
                        <Tile label="Contracts" value={sm.contracts} />
                        <Tile label="To charge (AED)" value={money(sm.asked)} tone="text-gray-900" />
                        <Tile label="Charged (AED)" value={money(sm.paid)} tone="text-green-700" />
                        <Tile label="Still to charge (AED)" value={money(sm.still)} tone="text-red-700" />
                        <Tile label="Not charged" value={sm.not_charged} tone="text-red-700" />
                        <Tile label="Partly charged" value={sm.partly} tone="text-amber-700" />
                        <Tile label="Cleared without a payment" value={sm.cleared} tone="text-purple-700" />
                        <Tile label="Charged" value={sm.charged} tone="text-green-700" />
                    </div>
                    <TableContainer maxHeight="72vh" className="overflow-auto">
                        <Table sticky className="min-w-full">
                            <TableHead>
                                <TableRow>
                                    {HEADS.map((h) => <TableHeader key={h} className="whitespace-nowrap">{h}</TableHeader>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.map((r, i) => {
                                    const v = shown(r);
                                    const [kLabel, kTone] = KIND[r.deposit?.kind] || KIND.unknown;
                                    return (
                                        <TableRow key={`${r.day}-${r.branch}-${r.contract}`} className={v.result === 'charged' ? 'bg-green-50' : ''}>
                                            <TableCell><CellNumber value={i + 1} /></TableCell>
                                            <TableCell>
                                                <span className="ds-cell-date">{day(r.day)}</span>
                                                <CellMuted>{r.emailed ? 'e-mailed' : 'no e-mail'}</CellMuted>
                                            </TableCell>
                                            <TableCell><Badge variant={r.branch === 'T2' ? 'info' : 'accent'} size="sm">{r.branch}</Badge></TableCell>
                                            <TableCell className="whitespace-nowrap"><CellId>{r.contract}</CellId><CellMuted>{r.plate}</CellMuted></TableCell>
                                            <TableCell><CellText>{r.customer}</CellText></TableCell>
                                            <TableCell><Badge variant={kTone} size="sm">{kLabel}{r.deposit?.amount ? ` · ${money(r.deposit.amount)}` : ''}</Badge></TableCell>
                                            <TableCell><span className="ds-cell-date">{(r.sentOn || []).map(day).join(', ')}</span></TableCell>
                                            <TableCell className="ds-cell-num">{money(r.toCollect)}</TableCell>
                                            <TableCell className={`ds-cell-num ${toneClass(v.paid > 0.05 ? 'success' : undefined)}`}>{money(v.paid)}</TableCell>
                                            <TableCell>
                                                {v.payments.length
                                                    ? v.payments.map((p, k) => (
                                                        <div key={k} className="text-xs whitespace-nowrap">
                                                            {[when(p.at), who(p.by), p.method, money(p.amount)].filter(Boolean).join(' · ')}
                                                        </div>
                                                    ))
                                                    : <CellMuted>—</CellMuted>}
                                            </TableCell>
                                            <TableCell className={`ds-cell-num ${toneClass(v.stillOwed > 0.05 ? 'danger' : undefined)}`}>{money(v.stillOwed)}</TableCell>
                                            <TableCell>
                                                <StatusBadge status={v.result} tone={v.tone} label={v.label} />
                                                {v.notes.map((n, k) => <CellMuted key={k}>{n}</CellMuted>)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {list.length > 0 && (
                                    <TableRow className="is-total">
                                        <TableCell colSpan={7}>TOTAL · {sm.contracts} contracts</TableCell>
                                        <TableCell className="ds-cell-num">{money(sm.asked)}</TableCell>
                                        <TableCell className={`ds-cell-num ${toneClass('success')}`}>{money(sm.paid)}</TableCell>
                                        <TableCell />
                                        <TableCell className={`ds-cell-num ${toneClass('danger')}`}>{money(sm.still)}</TableCell>
                                        <TableCell>{sm.not_charged + sm.partly} not fully charged</TableCell>
                                    </TableRow>
                                )}
                                {!list.length && (
                                    <TableRow>
                                        <TableCell colSpan={HEADS.length}>
                                            <EmptyState icon={ICONS.mail} title={filtered ? 'No contracts match the filters' : 'No reminder results yet'}
                                                description={filtered ? 'Change or clear the filters.' : 'The follow-up runs every day at 10:00 for the contracts on their last day (day 14).'} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </div>
    );
}
